<template>
    <SbsPopper
        ref="popper"
        theme="sbs-tooltip"
        :placement="position"
        :triggers="triggers"
        :strategy="strategy"
        :delay="delay"
        :distance="offset"
        :disabled="disabled"
        :container="parentSelector"
        :shown="isMenuShown"
        :fullWidth="fullWidth"
        @show="$emit('show')"
        @hide="$emit('hide')">
        <template #default>
            <div
                class="sbs-popper__activator sbs-tooltip__activator"
                @mouseenter="handleActivatorMouseOver"
                @mouseleave="handleActivatorMouseLeave">
                <slot></slot>
            </div>
        </template>
        <template #content>
            <div
                :class="tooltipClasses"
                :style="dimensionStyles"
                @mouseenter="handleContentMouseOver"
                @mouseleave="handleActivatorMouseLeave">
                <div
                    class="sbs-tooltip__content"
                    :class="tooltipContentClass">
                    <slot name="content"> {{ content }} </slot>
                </div>
                <div
                    v-if="showArrow"
                    class="sbs-tooltip__arrow"></div>
            </div>
        </template>
    </SbsPopper>
</template>

<script>
import dimensionsMixin from '../../mixins/dimensions.js';
import {
    AllPositions,
    FLOAT_POSITIONS,
} from '../../utils/positions.js';
import { AllTransitions } from '../../utils/transitions.js';
import {
    isArrayContainsValue,
    isValueConvertedToNumber,
} from '../../utils/validators.js';
import SbsPopper from '../popper/SbsPopper.vue';


/**
 * Компонент тултипа
 */
export default {
    name: 'SbsTooltip',
    components: {
        SbsPopper,
    },
    mixins: [dimensionsMixin],
    data: () => ({
        isPopupOpen: false,
        timeoutId: null,
    }),
    emits: ['opened', 'closed', 'show', 'hide'],
    computed: {
        strategy() {
            return this.fixed ? 'fixed' : 'absolute';
        },
        delay() {
            return {
                show: this.openDelay,
                hide: this.closeDelay,
            };
        },
        tooltipClasses() {
            return [
                `sbs-tooltip sbs-tooltip--${this.position}`,
                this.tooltipClass,
                { 'sbs-tooltip--arrow': this.showArrow },
            ];
        },
        triggers() {
            if (this.preventCloseOnHover) return [];

            return ['focus', 'hover'];
        },
        isMenuShown() {
            if (!this.preventCloseOnHover) return null;

            return this.isPopupOpen;
        },
    },
    methods: {
        handleActivatorMouseOver() {
            if (!this.preventCloseOnHover) return;

            this.isPopupOpen = true;
        },
        handleActivatorMouseLeave() {
            if (!this.preventCloseOnHover) return;

            this.timeoutId = setTimeout(() => {
                this.isPopupOpen = false;
            }, this.closeDelay);
        },
        handleContentMouseOver() {
            if (!this.preventCloseOnHover || !this.timeoutId || !this.isPopupOpen) return;

            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        },
    },
    watch: {
        isPopupOpen(value) {
            this.$emit(value ? 'opened' : 'closed');
        },
    },
    props: {
        /**
         * Задержка перед скрытием тултипа
         */
        closeDelay: {
            type: [Number, String],
            default: 150,
            validator: isValueConvertedToNumber,
        },
        /**
         * Контент тултипа
         */
        content: {
            type: String,
            default: '',
        },
        /**
         * Отключает появление тултипа
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Устанавливает `position: fixed` для тултипа
         */
        fixed: {
            type: Boolean,
            default: false,
        },
        /**
         * Отступ между тултипом и активатором
         */
        offset: {
            type: [Number, String],
            default: 10,
            validator: isValueConvertedToNumber,
        },
        /**
         * Задержка перед появлением тултипа
         */
        openDelay: {
            type: [Number, String],
            default: 50,
            validator: isValueConvertedToNumber,
        },
        /**
         * Селектор контейнера, куда будет рендериться тултип
         *
         * Если не указан, то будет использован `document.body`
         */
        parentSelector: {
            type: String,
            default: 'body',
        },
        /**
         * Выравнивание тултипа относительно активатора
         *
         * Валидны следующие значения позиции: `top`, `right`, `bottom`, `left`
         *
         * Также можно указать модификаторы для выравнивания
         * относительно края: `-start`, `-end`
         *
         * Примеры использования: `top`, `left-start`, `bottom-end`
         */
        position: {
            type: String,
            default: AllPositions.BOTTOM,
            validator: isArrayContainsValue(FLOAT_POSITIONS),
        },
        /**
         * Определяет будет ли скрываться тултип при наведении на него
         */
        preventCloseOnHover: {
            type: Boolean,
            default: false,
        },
        /**
         * Определяет видимость стрелки у тултипа
         */
        showArrow: {
            type: Boolean,
            default: false,
        },
        /**
         * Класс для тултипа
         */
        tooltipClass: {
            type: String,
            default: '',
        },
        /**
         * Класс для контента тултипа
         */
        tooltipContentClass: {
            type: String,
            default: '',
        },
        /**
         * Название класса для анимации открытия/закрытия тултипа
         */
        transitionName: {
            type: String,
            default: AllTransitions.FADE,
        },
        /**
         * `z-index`, используемый для тултипа
         */
        zIndex: {
            type: [Number, String],
            default: 100,
            validator: isValueConvertedToNumber,
        },
        /**
         * `full-wdth`, определяет, расстягивается ли контейнер тултипа на всю ширину
         */
        fullWidth: {
            type: Boolean,
            default: false,
        },
    },
};
</script>

<style lang="scss">
.v-popper--theme-sbs-tooltip {
    display: flex;

    .v-popper__inner {
        padding: 0;
        overflow: hidden;
        background-color: transparent;
    }

    .v-popper__arrow-container {
        display: none;
    }
}

.sbs-tooltip {
    display: flex;
    flex-direction: column;
    padding: 8px 12px;
    color: $white;
    text-align: center;
    background-color: $black;
    border-radius: $rounded-10;
    opacity: 0.9;

    @include typo-secondary-book;

    &--top-start.sbs-tooltip--arrow,
    &--right-end.sbs-tooltip--arrow {
        border-bottom-left-radius: 0;
    }

    &--top-end.sbs-tooltip--arrow,
    &--left-end.sbs-tooltip--arrow {
        border-bottom-right-radius: 0;
    }

    &--right-start.sbs-tooltip--arrow,
    &--bottom-start.sbs-tooltip--arrow {
        border-top-left-radius: 0;
    }

    &--bottom-end.sbs-tooltip--arrow,
    &--left-start.sbs-tooltip--arrow {
        border-top-right-radius: 0;
    }

    &.hoverable {
        pointer-events: all;
    }

    &__content {
        display: flex;
        flex-direction: column;
        word-wrap: break-word;
    }

    &__activator {
        display: flex;
    }

    &__arrow {
        position: absolute;
        display: flex;
        width: 0;
        height: 0;
        color: $black;
        border-style: solid;

        .sbs-tooltip--top-start & {
            top: 100%;
            left: 0;
            border-color: $current-color transparent transparent;
            border-width: 5px 5px 0 0;
        }

        .sbs-tooltip--top & {
            top: 100%;
            left: 50%;
            border-color: $current-color transparent transparent;
            border-width: 5px 5px 0;
            transform: translateX(-50%);
        }

        .sbs-tooltip--top-end & {
            top: 100%;
            right: 0;
            border-color: transparent $current-color transparent transparent;
            border-width: 0 5px 5px 0;
        }

        .sbs-tooltip--right-start & {
            top: 0;
            right: 100%;
            border-color: transparent $current-color transparent transparent;
            border-width: 0 5px 5px 0;
        }

        .sbs-tooltip--right & {
            top: 50%;
            right: 100%;
            border-color: transparent $current-color transparent transparent;
            border-width: 5px 5px 5px 0;
            transform: translateY(-50%);
        }

        .sbs-tooltip--right-end & {
            right: 100%;
            bottom: 0;
            border-color: transparent transparent $current-color;
            border-width: 0 0 5px 5px;
        }

        .sbs-tooltip--bottom-start & {
            bottom: 100%;
            left: 0;
            border-color: transparent transparent transparent $current-color;
            border-width: 5px 0 0 5px;
        }

        .sbs-tooltip--bottom & {
            bottom: 100%;
            left: 50%;
            border-color: transparent transparent $current-color;
            border-width: 0 5px 5px;
            transform: translateX(-50%);
        }

        .sbs-tooltip--bottom-end & {
            right: 0;
            bottom: 100%;
            border-color: transparent transparent $current-color;
            border-width: 0 0 5px 5px;
        }

        .sbs-tooltip--left-start & {
            top: 0;
            left: 100%;
            border-color: $current-color transparent transparent;
            border-width: 5px 5px 0 0;
        }

        .sbs-tooltip--left & {
            top: 50%;
            left: 100%;
            border-color: transparent transparent transparent $current-color;
            border-width: 5px 0 5px 5px;
            transform: translateY(-50%);
        }

        .sbs-tooltip--left-end & {
            bottom: 0;
            left: 100%;
            border-color: transparent transparent transparent $current-color;
            border-width: 5px 0 0 5px;
        }
    }
}
</style>
